<template>
  <section id="contact" class="py-16 sm:py-24 bg-gray-50 dark:bg-gray-800/50 relative overflow-hidden transition-colors duration-500">
    <div class="max-w-5xl mx-auto px-6 relative z-10">
      <!-- Section header -->
      <div class="mb-10 sm:mb-14">
        <span class="inline-block px-3 py-1 bg-amber-100/80 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-semibold text-xs rounded-full mb-3">{{ t('contactLabel') }}</span>
        <h2 class="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white tracking-tight mb-3">
          {{ t('lets') }} <span class="gradient-text">{{ t('workTogether') }}</span>
        </h2>
        <p class="text-base text-gray-600 dark:text-gray-400 max-w-lg">
          {{ t('contactSubtitle') }}
        </p>
      </div>

      <!-- Contact links -->
      <div class="grid sm:grid-cols-3 gap-4 mb-10">
        <a
          href="mailto:pvpmartins@gmail.com"
          class="group flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-amber-200 dark:hover:border-amber-800 transition-colors"
        >
          <div class="w-10 h-10 shrink-0 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
            <svg class="w-5 h-5 text-amber-700 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
          </div>
          <div class="min-w-0">
            <h3 class="font-semibold text-gray-900 dark:text-white text-sm">{{ t('email') }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 truncate">pvpmartins@gmail.com</p>
          </div>
        </a>

        <a
          href="https://linkedin.com/in/pvpmartins"
          target="_blank"
          class="group flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-amber-200 dark:hover:border-amber-800 transition-colors"
        >
          <div class="w-10 h-10 shrink-0 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
            <svg class="w-5 h-5 text-amber-700 dark:text-amber-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </div>
          <div>
            <h3 class="font-semibold text-gray-900 dark:text-white text-sm">{{ t('linkedin') }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('linkedinDesc') }}</p>
          </div>
        </a>

        <a
          href="https://github.com/pvpmartins"
          target="_blank"
          class="group flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-amber-200 dark:hover:border-amber-800 transition-colors"
        >
          <div class="w-10 h-10 shrink-0 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
            <svg class="w-5 h-5 text-amber-700 dark:text-amber-400" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
            </svg>
          </div>
          <div>
            <h3 class="font-semibold text-gray-900 dark:text-white text-sm">{{ t('github') }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('githubDesc') }}</p>
          </div>
        </a>
      </div>

      <!-- CTA -->
      <div>
        <a
          href="mailto:pvpmartins@gmail.com"
          class="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 text-sm"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
          </svg>
          {{ t('sendMessage') }}
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useI18n } from '@/i18n'

const { t } = useI18n()
</script>
